import { storeClosedModalAtom } from "@/atoms/store-closed-modal";
import { FeedbackIcon } from "@/components/feedback";
import { BottomSheet } from "@/components/modals/BottomSheet";
import { Button, Heading, SHEET_GUTTER, Text } from "@/components/ui";
import { useMerchantStatus } from "@/hooks/use-merchant-status";
import { useStorefrontScheduling } from "@/hooks/use-storefront-scheduling";
import { formatWindowOpening } from "@/lib/scheduling/format";
import * as Dialog from "@radix-ui/react-dialog";
import { useAtom } from "jotai";
import { cn } from "@/lib/cn";
import { Clock } from "@/icons";

/**
 * Bottom-sheet shown when a customer tries to add an item while the bag is
 * blocked. Mirrors iFood's pattern: it blocks the addition and invites the
 * customer to keep browsing the menu.
 *
 * Four reasons reach it (§2A.5): the store is simply closed and the channel
 * has no scheduling, the target window has no slots left, the merchant has
 * no operating window in the next seven days, or the store is paused.
 *
 * Mounted once globally (see Router) and driven by `storeClosedModalAtom`.
 * The shared BottomSheet shell handles the `modal={false}` + manual backdrop
 * + z-index composition with the full-screen ItemModal.
 */
export default function StoreClosedModal() {
  const [open, setOpen] = useAtom(storeClosedModalAtom);
  const { nextOpening } = useMerchantStatus();
  const scheduling = useStorefrontScheduling();

  const block = scheduling.block;
  const windowFull = block?.reason === "WINDOW_FULL";
  const noWindow = block?.reason === "NO_UPCOMING_WINDOW";
  const paused = block?.reason === "PAUSED";
  const returnsAt = block?.nextOpeningAt
    ? formatWindowOpening(block.nextOpeningAt, new Date(), scheduling.timezone ?? undefined)
    : null;

  const title = windowFull
    ? "Não há mais horários nessa janela"
    : paused
      ? "Loja pausada temporariamente"
      : noWindow
        ? "Sem horários de funcionamento"
        : nextOpening
          ? `Esta loja abre ${nextOpening.dayLabel} às`
          : "Esta loja está fechada no momento";

  const body = windowFull
    ? returnsAt
      ? `Os horários dessa janela acabaram. A próxima abertura é ${returnsAt}.`
      : "Os horários dessa janela acabaram. Volte mais tarde para agendar na próxima."
    : paused
      ? returnsAt
        ? `A loja pausou os pedidos por um momento e volta ${returnsAt}. Você pode olhar o cardápio à vontade.`
        : "A loja pausou os pedidos por um momento. Você pode olhar o cardápio à vontade e voltar daqui a pouco."
      : noWindow
        ? "Esta loja ainda não tem horários de funcionamento cadastrados para os próximos dias."
        : "Você pode olhar o cardápio à vontade e voltar quando a loja abrir.";

  const showBigClock = !windowFull && !noWindow && !paused && Boolean(nextOpening);

  return (
    <BottomSheet open={open} onOpenChange={setOpen}>
      <div className={cn("mx-auto flex w-full max-w-md flex-col items-center pb-safe pt-6 text-center", SHEET_GUTTER)}>
        <FeedbackIcon variant="warning" icon={Clock} className="mb-4" />
        <div className="flex flex-col items-center gap-0.5">
          <Dialog.Title asChild>
            <Heading
              level={2}
              size={{ base: "title", sm: "page" }}
              weight={showBigClock ? "medium" : "semibold"}
              tone={showBigClock ? "muted" : "default"}
              leading="snug"
            >
              {title}
            </Heading>
          </Dialog.Title>
          {showBigClock && nextOpening && (
            <Text as="span" size="4xl" weight="bold" leading="none">
              {nextOpening.time}
            </Text>
          )}
        </div>

        <Text size={{ base: "base", sm: "lg" }} leading="relaxed" tone="muted" className="mt-5 md:mt-6">
          {body}
        </Text>

        <Dialog.Close asChild>
          <Button fullWidth className="mt-7 md:mt-8">
            Ok, entendi
          </Button>
        </Dialog.Close>
      </div>
    </BottomSheet>
  );
}
